/**
 * Skimmer Sync Customer Mapping Failure Exception Config
 *
 * Covers: ex-client-sk-ls-customer-sync-failure
 *
 * Skimmer customers the external sync tool could not match to a
 * Lightspeed customer. Backend reads from franchise_exception_tags +
 * exception_metadata (no ls_customers join) - object_id is the Skimmer
 * customer ID extracted from the Drive CSV's URL segment, with a
 * '__missing_id__' fallback for the known mangled-URL cases.
 */
import React from 'react'
import { Chip, Link, Stack, Tooltip, Typography } from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { textPart } from '../shared/rowSubtitle'
import {
  renderDaysOpen,
  renderLastReportedLink,
} from '../shared/skimmerSyncRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { formatBusinessDate } from '../../../utils/inspectorFormatters'
import { ownerPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'

const renderDate = (value: any): React.ReactNode => formatBusinessDate(value)

const renderOwnerChip = (value: any, row: Record<string, any>): React.ReactNode => {
  const isFp = ((row.owner || 'fp') as string) === 'fp'
  return withTooltip(
    {
      title: isFp
        ? 'You can fix this in Skimmer or Lightspeed.'
        : 'PSC will resolve this - no FP action required.',
    },
    React.createElement(
      Chip,
      ownerPillChipProps(isFp ? 'fp' : 'psc', isFp ? 'FP' : 'PSC'),
    ),
  )
}

const renderCustomer = (value: any, row: Record<string, any>): React.ReactNode => {
  const id = row.skimmer_customer_id || row.object_id || '-'
  const label = id === '__missing_id__'
    ? 'Client ID missing (URL truncated in source CSV)'
    : id
  if (!row.skimmer_url) {
    return label
  }
  return React.createElement(
    Link,
    {
      href: row.skimmer_url,
      target: '_blank',
      rel: 'noopener noreferrer',
      sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 },
    },
    label,
    React.createElement(OpenInNewIcon, { fontSize: 'inherit' }),
  )
}

// Cap the in-table chip row so a bucket row with ~100 surfacing invoices
// (e.g. the uswea __missing_id__ fallback) doesn't blow the row height.
// The full list lives in the drawer Details section.
const INVOICE_CHIP_CAP = 8

const renderInvoiceList = (value: any, row: Record<string, any>): React.ReactNode => {
  const list: string[] = Array.isArray(row.invoice_numbers) ? row.invoice_numbers : []
  if (list.length === 0) return '-'
  const shown = list.slice(0, INVOICE_CHIP_CAP)
  const overflow = list.length - shown.length
  const chips: React.ReactNode[] = shown.map((inv, idx) =>
    React.createElement(Chip, {
      key: idx,
      label: inv,
      size: 'small',
      variant: 'outlined',
    }),
  )
  if (overflow > 0) {
    chips.push(
      React.createElement(Chip, {
        key: 'more',
        label: `+${overflow} more`,
        size: 'small',
        variant: 'outlined',
        sx: { color: 'text.secondary', borderStyle: 'dashed' },
      }),
    )
  }
  return React.createElement(
    Stack,
    { direction: 'row', spacing: 0.5, flexWrap: 'wrap', useFlexGap: true },
    ...chips,
  )
}

const renderLastSeenCsv = (value: any, row: Record<string, any>): React.ReactNode => {
  const filename = row.last_seen_csv || value
  if (!filename) {
    return React.createElement(
      Typography,
      { variant: 'caption', color: 'text.secondary' },
      '-',
    )
  }
  if (!row.last_seen_csv_url) {
    return React.createElement(
      Typography,
      { variant: 'caption', color: 'text.secondary' },
      filename,
    )
  }
  return React.createElement(
    Link,
    {
      href: row.last_seen_csv_url,
      target: '_blank',
      rel: 'noopener noreferrer',
      variant: 'caption',
      sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 },
    },
    filename,
    React.createElement(OpenInNewIcon, { fontSize: 'inherit' }),
  )
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'owner',
    label: 'Action By',
    align: 'center',
    contexts: ['inspector'],
    render: renderOwnerChip,
  },
  {
    key: 'skimmer_customer_id',
    label: 'Skimmer Client',
    contexts: ['inspector'],
    render: renderCustomer,
    getSortValue: (row) => row.skimmer_customer_id || row.object_id || '',
  },
  {
    key: 'customer_name',
    label: 'Name',
    contexts: ['inspector'],
    render: (value) => value || '-',
  },
  {
    key: 'invoice_numbers',
    label: 'Surfacing Invoices',
    contexts: ['inspector'],
    render: renderInvoiceList,
  },
  {
    key: 'first_reported_at',
    label: 'First Reported',
    align: 'center',
    contexts: ['inspector'],
    render: renderDate,
    getSortValue: (row) => row.first_reported_at || '',
  },
  {
    key: 'last_reported_at',
    label: 'Last Reported',
    align: 'center',
    contexts: ['inspector'],
    render: renderDate,
    getSortValue: (row) => row.last_reported_at || '',
  },
  {
    key: 'last_seen_csv',
    label: 'Last CSV',
    contexts: ['inspector'],
    render: renderLastSeenCsv,
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'skimmer_customer_id',
    label: 'Skimmer Client',
    contexts: ['platform'],
    render: renderCustomer,
    getSortValue: (row) => row.skimmer_customer_id || row.object_id || '',
  },
  {
    key: 'customer_name',
    label: 'Client Name',
    contexts: ['platform'],
    render: (value) => value || '-',
  },
  {
    key: 'title',
    label: 'Failure',
    contexts: ['platform'],
    render: (value) => value || '-',
  },
  {
    key: 'invoice_numbers',
    label: 'Failure Details',
    align: 'left',
    contexts: ['platform'],
    render: (_value, row) => {
      const list: string[] = Array.isArray(row.invoice_numbers) ? row.invoice_numbers : []
      return list.length ? list.join(', ') : '-'
    },
  },
  {
    key: 'owner',
    label: 'Action By',
    align: 'center',
    contexts: ['platform'],
    render: renderOwnerChip,
  },
  {
    key: 'days_open',
    label: 'Days Open',
    align: 'center',
    contexts: ['platform'],
    render: renderDaysOpen,
    getSortValue: (row) => (typeof row.days_open === 'number' ? row.days_open : -1),
  },
  {
    key: 'last_reported_at',
    label: 'Last Reported',
    align: 'center',
    contexts: ['platform'],
    render: renderLastReportedLink,
    getSortValue: (row) => row.last_reported_at || '',
    exportFormat: 'date',
  },
]

export const skimmerSyncCustomerConfig: ExceptionConfig = {
  codes: ['ex-client-sk-ls-customer-sync-failure'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'last_reported_at',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  // Canonical tag id keys the row (unique + stable); backend returns one
  // canonical row per object - no JS dedupe.
  rowKeyField: 'exception_tag_id',
  getRowTitle: row =>
    textPart(row.customer_name) ||
    textPart(row.skimmer_customer_id) ||
    `Customer ${String(row.object_id ?? '').slice(0, 8)}`,
}
